/**
 * MediaAnalysisOps.tsx — the media-analysis operations page (CONTRACTS.md section 16.2; card MA-86), routed at
 * `/media-analysis`, admins only.
 *
 * Reads two admin queries: `mediaAnalysis.health.getOperationsView` (the health snapshot of section 15.1, every open
 * stage job — a job still stopping is marked "Deteniéndose" —, the runs that failed in the last 7 days with their
 * reasons, the runs running now and the switches of section 9.16) and
 * `mediaAnalysis.costs.getMonthSummary` (section 9.15: the month's spend per ledger line against the budget, today's
 * manual spend against its ceiling, the cost per analysed content-hour).
 *
 * Two controls, both admins only: a channel picker with "Analizar primero" (`dispatch.startRunsForChannel`, section
 * 9.14 "Channels first") and "Cancelar" on a running run (`runs.cancelRun`). Both go through `useMutationWithToast`
 * (argument keys only are logged). For a member who is not an admin they are DISABLED with a tooltip, never hidden.
 * The switches are read-only values: an owner changes them with the Convex dashboard's function runner.
 *
 * Honest-state contract: `undefined` renders a skeleton, an empty list its empty state, a thrown query error reaches
 * the toast through the router's boundary. The routed component wraps the body in `AdminGate` (the admin-only screen
 * guard); the body is exported on its own so the controls' disabled state can be exercised with a non-admin member.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  AdminGate, Btn, EmptyState, Field, PageHead, Panel, Pill, Progress, Select, Skeleton, SkeletonStatRow, SkeletonTable,
  StatCard, Toggle,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { useIsAdmin } from '../../hooks/useCurrentMember';
import { useChannelList } from '../../hooks/useChannelList';
import { money } from '../../lib/format';

// The generated `api` type carries the media-analysis modules only after a person runs codegen (rule 10); the runtime
// object resolves any path. The precedent is `foldersApi` in content/_bundle.tsx.
const mediaAnalysisApi: any = (api as any).mediaAnalysis;

/* ---------------- Words on screen (section 16.2) ---------------- */

export const FAILURE_WORDS: Record<string, string> = {
  frames_failed: 'Fallo en fotogramas',
  transcript_failed: 'Fallo en transcripción',
  captions_failed: 'Fallo en subtítulos',
  index_failed: 'Fallo en el índice',
  rendition_changed: 'Archivo reemplazado',
  asset_removed: 'Recurso eliminado',
  operator_cancelled: 'Cancelado por un operador',
  run_timeout: 'Tiempo agotado',
};

const JOB_STATUS_WORDS: Record<string, string> = {
  reserved: 'Reservado',
  submitted: 'Enviado',
  running: 'En ejecución',
  cancelling: 'Deteniéndose',
};

const STAGE_WORDS: Record<string, string> = {
  extract: 'Extracción',
  understand: 'Comprensión',
  caption: 'Subtítulos e índice',
  persist: 'Persistencia',
};

const ORIGIN_WORDS: Record<string, string> = { manual: 'manual', auto: 'automático', backfill: 'relleno', retry: 'reintento' };

const QUEUE_WORDS: Record<string, string> = { interactive: 'interactiva', background: 'de fondo' };

const SWITCH_LABELS: Record<string, string> = {
  analysisPipelineVersion: 'Versión del pipeline',
  analysisAutoDispatch: 'Despacho automático',
  analysisBackfillEnabled: 'Relleno de la biblioteca',
  analysisBackfillCursor: 'Cursor del relleno',
  analysisBackfillChannel: 'Canal en cola',
  analysisBackfillChannelCursor: 'Cursor del canal',
  analysisLibraryBudgetUsd: 'Presupuesto mensual (USD)',
  analysisManualDailyBudgetUsd: 'Límite diario manual (USD)',
  analysisSpeechConfiguration: 'Configuración de voz',
  analysisProseRate: 'Tasa de prosa',
  analysisEscalationRate: 'Tasa de escalada',
  analysisCaptionProvider: 'Proveedor de subtítulos',
  analysisCaptionModel: 'Modelo de subtítulos',
  analysisCaptionServiceTier: 'Nivel de servicio',
};

export const NOT_ADMIN_TOOLTIP = 'Solo un administrador puede usar este control.';
const RESOLVING_TOOLTIP = 'Comprobando permisos…';

export function failureWord(reason: string | null | undefined): string {
  if (!reason) return '—';
  return FAILURE_WORDS[reason] ?? reason;
}

/** An integer span in words: "3 d 2 h", "2 h 05 min", "4 min", "12 s". Integer arithmetic only. */
export function spanWords(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.trunc(ms / 1000);
  const days = Math.trunc(totalSeconds / 86_400);
  const hours = Math.trunc((totalSeconds % 86_400) / 3_600);
  const minutes = Math.trunc((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  if (minutes > 0) return `${minutes} min`;
  return `${seconds} s`;
}

function share(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value * 100)} %`;
}


export function currentMonth(now: number = Date.now()): string {
  return new Date(now).toISOString().slice(0, 7);
}

/* ---------------- Types (mirrors of the two queries' answers) ---------------- */

type Percentiles = { count: number; medianMs: number | null; p95Ms: number | null };

export interface OperationsViewShape {
  at: number;
  snapshot: {
    windowMs: number;
    runs: { created: number; completed: number; cancelled: number; running: number; failed: Record<string, number>; wall: Percentiles; stuck: { runId: string; stage: string; ageMs: number }[] };
    jobs: { finishedTotal: number; failedTotal: number; cancelled: number; multiAttempt: number; byErrorClass: Record<string, number>; startDelay: Percentiles };
    openJobs: { total: number; truncated: boolean; byQueue: Record<string, { count: number; oldestAgeMs: number | null; oldestStatus: string | null }> };
    reconcile: { lastTickAt: number | null; ageMs: number | null };
    purges: { open: number; stalled: number; oldestAgeMs: number | null };
    cost: { lines: number; spendUsd: number; captionCalls: number; rateLimitedCalls: number; rateLimitedShare: number | null; inputTokens: number; cachedTokens: number; cachedShare: number | null; reasoningTokens: number };
    truncated: string[];
  };
  openJobs: { rows: { jobRowId: string; runId: string; stage: string; status: string; queue: string; awsJobId: string | null; attempts: number; ageMs: number; sinceUpdateMs: number }[]; truncated: boolean };
  failedRuns: { rows: { runId: string; assetId: string; stage: string; origin: string; failureReason: string; createdAt: number; completedAt: number | null }[]; windowMs: number; truncated: boolean };
  runningRuns: { rows: { runId: string; assetId: string; stage: string; stageState: string; origin: string; createdAt: number; ageMs: number }[]; truncated: boolean };
  switches: Record<string, string | number | boolean | null>;
}

export interface MonthSummaryShape {
  month: string;
  usd: number;
  lines: number;
  byLine: Record<string, number>;
  manualTodayUsd: number;
  budgetUsd: number;
  manualCeilingUsd: number;
  projectedUsd: number;
  runsInFlight: number;
  analysed: { runs: number; contentMs: number; measuredUsd: number; truncated: boolean };
}

const LINE_WORDS: Record<string, string> = {
  extract_compute: 'Cómputo de extracción',
  understand_compute: 'Cómputo de comprensión',
  caption_compute: 'Cómputo de subtítulos',
  caption_tokens: 'Tokens de subtítulos',
  escalation_tokens: 'Tokens de escalada',
};

function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(100, Math.round((part / whole) * 100));
}

/**
 * The cost lines of section 16.2 ("Cost" and the operations page share them): the month's spend per ledger line, the
 * budget and the projected spend, today's manual spend against its ceiling, the cost per analysed content-hour.
 */
export function MediaAnalysisCostLines({ summary }: { summary: MonthSummaryShape }) {
  const lines = Object.entries(summary.byLine).sort(([a], [b]) => a.localeCompare(b));
  // Display only: the integer span becomes hours for the "per content-hour" figure.
  const contentHours = summary.analysed.contentMs / 3_600_000;
  const perHour = contentHours > 0 ? summary.analysed.measuredUsd / contentHours : null;
  return (
    <div data-testid="media-analysis-cost-lines" style={{ display: 'grid', gap: 14 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span>Gasto del mes {summary.month}</span>
          <span data-testid="cost-month-spend">{money(summary.usd)} de {money(summary.budgetUsd)}</span>
        </div>
        <Progress value={percent(summary.usd, summary.budgetUsd)} color={summary.usd >= summary.budgetUsd ? 'var(--bad)' : 'var(--deep-green)'} />
        <div style={{ fontSize: 12, color: 'var(--muted-slate)', marginTop: 6 }}>
          Proyección con {summary.runsInFlight} en curso: <span data-testid="cost-projected">{money(summary.projectedUsd)}</span>
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span>Análisis manuales de hoy</span>
          <span data-testid="cost-manual-today">{money(summary.manualTodayUsd)} de {money(summary.manualCeilingUsd)}</span>
        </div>
        <Progress value={percent(summary.manualTodayUsd, summary.manualCeilingUsd)} color={summary.manualTodayUsd >= summary.manualCeilingUsd ? 'var(--bad)' : 'var(--cta)'} />
      </div>
      <div style={{ fontSize: 13 }}>
        Costo por hora de contenido analizada:{' '}
        <strong data-testid="cost-per-hour">{perHour === null ? '—' : money(perHour)}</strong>
        <span style={{ color: 'var(--muted-slate)' }}> ({summary.analysed.runs} análisis completados, {spanWords(summary.analysed.contentMs)} de contenido{summary.analysed.truncated ? ', lectura acotada' : ''})</span>
      </div>
      <div>
        <div style={{ fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', marginBottom: 6 }}>Por línea del libro</div>
        {lines.length === 0
          ? <div data-testid="cost-lines-empty" style={{ fontSize: 13, color: 'var(--muted-slate)' }}>Sin líneas de costo este mes.</div>
          : lines.map(([line, usd]) => (
            <div key={line} data-testid="cost-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
              <span>{LINE_WORDS[line] ?? line}</span>
              <span>{money(usd)}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------------- The page body ---------------- */

const TABLE_HEAD: React.CSSProperties = {
  display: 'grid', padding: '10px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12,
  color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase',
};
const TABLE_ROW: React.CSSProperties = { display: 'grid', padding: '12px 20px', alignItems: 'center', borderBottom: '1px solid var(--lightest-gray)', fontSize: 13 };

export function MediaAnalysisOpsBody() {
  const isAdmin = useIsAdmin();
  const view = useQuery(mediaAnalysisApi.health.getOperationsView, {}) as OperationsViewShape | undefined;
  const summary = useQuery(mediaAnalysisApi.costs.getMonthSummary, { month: currentMonth() }) as MonthSummaryShape | undefined;
  const channels = useChannelList();
  const [channelId, setChannelId] = useState('');

  const startRuns = useMutationWithToast(mediaAnalysisApi.dispatch.startRunsForChannel, {
    logLabel: 'media_analysis_start_runs_for_channel',
    successMessage: 'Canal en cola: sus recursos se analizan primero.',
    errorMessagePrefix: 'No se pudo poner el canal en cola',
  });
  const cancel = useMutationWithToast(mediaAnalysisApi.runs.cancelRun, {
    logLabel: 'media_analysis_cancel_run',
    successMessage: 'Análisis cancelado.',
    errorMessagePrefix: 'No se pudo cancelar el análisis',
  });

  const controlsDisabled = isAdmin !== true;
  const controlTooltip = isAdmin === undefined ? RESOLVING_TOOLTIP : isAdmin ? undefined : NOT_ADMIN_TOOLTIP;

  const channelOptions = [{ value: '', label: 'Elegir un canal…' }, ...(channels ?? []).map((c) => ({ value: c._id, label: c.name }))];

  return (
    <div data-testid="media-analysis-ops">
      <PageHead title="Operaciones de análisis de medios" description="Estado del pipeline de análisis, trabajos abiertos, fallos recientes, costo y configuración." />

      {/* 1. The health snapshot */}
      <section aria-label="Salud" style={{ marginBottom: 24 }}>
        {view === undefined ? <SkeletonStatRow n={4} /> : (
          <>
            <div className="circa-grid-4" style={{ marginBottom: 12 }}>
              <StatCard label="Análisis creados (24 h)" value={String(view.snapshot.runs.created)} hint={`${view.snapshot.runs.running} en curso`} />
              <StatCard label="Completados (24 h)" value={String(view.snapshot.runs.completed)} hint={`mediana ${spanWords(view.snapshot.runs.wall.medianMs)} · p95 ${spanWords(view.snapshot.runs.wall.p95Ms)}`} />
              <StatCard label="Fallidos (24 h)" value={String(Object.values(view.snapshot.runs.failed).reduce((a, b) => a + b, 0))} hint={`${view.snapshot.runs.cancelled} cancelados · ${view.snapshot.runs.stuck.length} atascados`} />
              <StatCard label="Trabajos abiertos" value={String(view.snapshot.openJobs.total)} hint={`interactiva ${view.snapshot.openJobs.byQueue.interactive?.count ?? 0} (más antiguo ${spanWords(view.snapshot.openJobs.byQueue.interactive?.oldestAgeMs)}) · de fondo ${view.snapshot.openJobs.byQueue.background?.count ?? 0} (más antiguo ${spanWords(view.snapshot.openJobs.byQueue.background?.oldestAgeMs)})`} />
            </div>
            <div className="circa-grid-4">
              <StatCard label="Trabajos de etapa (24 h)" value={`${view.snapshot.jobs.failedTotal} / ${view.snapshot.jobs.finishedTotal}`} hint={`fallidos / terminados · ${view.snapshot.jobs.multiAttempt} con más de un intento · demora de inicio p95 ${spanWords(view.snapshot.jobs.startDelay.p95Ms)}`} />
              <StatCard label="Última reconciliación" value={spanWords(view.snapshot.reconcile.ageMs)} hint={view.snapshot.reconcile.lastTickAt === null ? 'sin tick registrado' : 'desde el último tick'} />
              <StatCard label="Purgas abiertas" value={String(view.snapshot.purges.open)} hint={`${view.snapshot.purges.stalled} estancadas · más antigua ${spanWords(view.snapshot.purges.oldestAgeMs)}`} />
              <StatCard label="Subtítulos (24 h)" value={money(view.snapshot.cost.spendUsd)} hint={`${view.snapshot.cost.captionCalls} llamadas · 429: ${share(view.snapshot.cost.rateLimitedShare)} · caché: ${share(view.snapshot.cost.cachedShare)} · razonamiento: ${view.snapshot.cost.reasoningTokens}`} />
            </div>
            {view.snapshot.truncated.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted-slate)' }}>Lecturas acotadas: {view.snapshot.truncated.join(', ')}</div>
            )}
          </>
        )}
      </section>

      {/* 2. The open stage jobs */}
      <Panel title="Trabajos de etapa abiertos" pad={0} style={{ marginBottom: 24 }}>
        {view === undefined ? <div style={{ padding: 16 }}><SkeletonTable rows={3} cols={5} /></div> : view.openJobs.rows.length === 0 ? (
          <div style={{ padding: 16 }}><EmptyState icon="refresh" title="Sin trabajos abiertos" body="Ningún trabajo de etapa está reservado, enviado, en ejecución o deteniéndose." /></div>
        ) : (
          <div>
            <div style={{ ...TABLE_HEAD, gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1fr 1fr' }}>
              <span>Etapa</span><span>Estado</span><span>Cola</span><span>AWS Batch</span><span>Antigüedad</span><span>Desde el último cambio</span>
            </div>
            {view.openJobs.rows.map((job) => (
              <div key={job.jobRowId} data-testid="open-job-row" style={{ ...TABLE_ROW, gridTemplateColumns: '1.2fr 1fr 1fr 1fr 1fr 1fr' }}>
                <span>{STAGE_WORDS[job.stage] ?? job.stage}</span>
                <span><Pill tone={job.status === 'cancelling' ? 'warn' : job.status === 'running' ? 'good' : 'neutral'}>{JOB_STATUS_WORDS[job.status] ?? job.status}</Pill></span>
                <span>{QUEUE_WORDS[job.queue] ?? job.queue}</span>
                <span className="t-mono" style={{ fontSize: 12 }}>{job.awsJobId ?? '—'}</span>
                <span>{spanWords(job.ageMs)}</span>
                <span>{spanWords(job.sinceUpdateMs)}</span>
              </div>
            ))}
            {view.openJobs.truncated && <div style={{ padding: '8px 20px', fontSize: 12, color: 'var(--muted-slate)' }}>Lectura acotada: hay más trabajos abiertos de los que se muestran.</div>}
          </div>
        )}
      </Panel>

      {/* 3. The runs: running (with Cancelar) and failed in the last 7 days */}
      <Panel title="Análisis en curso" pad={0} style={{ marginBottom: 24 }}>
        {view === undefined ? <div style={{ padding: 16 }}><SkeletonTable rows={2} cols={4} /></div> : view.runningRuns.rows.length === 0 ? (
          <div style={{ padding: 16 }}><EmptyState icon="film" title="Sin análisis en curso" body="Ningún análisis está en ejecución ahora." /></div>
        ) : (
          <div>
            <div style={{ ...TABLE_HEAD, gridTemplateColumns: '2fr 1fr 1fr 1fr 120px' }}>
              <span>Recurso</span><span>Etapa</span><span>Origen</span><span>Antigüedad</span><span />
            </div>
            {view.runningRuns.rows.map((run) => (
              <div key={run.runId} data-testid="running-run-row" style={{ ...TABLE_ROW, gridTemplateColumns: '2fr 1fr 1fr 1fr 120px' }}>
                <span className="t-mono" style={{ fontSize: 12 }}>{run.assetId}</span>
                <span>{STAGE_WORDS[run.stage] ?? run.stage}</span>
                <span>{ORIGIN_WORDS[run.origin] ?? run.origin}</span>
                <span>{spanWords(run.ageMs)}</span>
                <span>
                  <Btn size="sm" variant="danger" disabled={controlsDisabled || cancel.loading} title={controlTooltip} onClick={() => { void cancel.run({ runId: run.runId }).catch(() => undefined); }}>
                    Cancelar
                  </Btn>
                </span>
              </div>
            ))}
            {view.runningRuns.truncated && <div style={{ padding: '8px 20px', fontSize: 12, color: 'var(--muted-slate)' }}>Lectura acotada: hay más análisis en curso de los que se muestran.</div>}
          </div>
        )}
      </Panel>

      <Panel title="Fallos de los últimos 7 días" pad={0} style={{ marginBottom: 24 }}>
        {view === undefined ? <div style={{ padding: 16 }}><SkeletonTable rows={3} cols={4} /></div> : view.failedRuns.rows.length === 0 ? (
          <div style={{ padding: 16 }}><EmptyState icon="checkCircle" title="Sin fallos recientes" body="Ningún análisis falló en los últimos 7 días." /></div>
        ) : (
          <div>
            <div style={{ ...TABLE_HEAD, gridTemplateColumns: '2fr 1fr 1fr 1.5fr' }}>
              <span>Recurso</span><span>Etapa</span><span>Origen</span><span>Motivo</span>
            </div>
            {view.failedRuns.rows.map((run) => (
              <div key={run.runId} data-testid="failed-run-row" style={{ ...TABLE_ROW, gridTemplateColumns: '2fr 1fr 1fr 1.5fr' }}>
                <span className="t-mono" style={{ fontSize: 12 }}>{run.assetId}</span>
                <span>{STAGE_WORDS[run.stage] ?? run.stage}</span>
                <span>{ORIGIN_WORDS[run.origin] ?? run.origin}</span>
                <span>{failureWord(run.failureReason)}</span>
              </div>
            ))}
            {view.failedRuns.truncated && <div style={{ padding: '8px 20px', fontSize: 12, color: 'var(--muted-slate)' }}>Lectura acotada: hay más fallos de los que se muestran.</div>}
          </div>
        )}
      </Panel>

      {/* 4. Channels first */}
      <Panel title="Analizar un canal primero" style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 13, color: 'var(--muted-slate)', marginTop: 0 }}>
          Pone en cola los recursos del canal para el relleno; sus análisis respetan los límites del relleno y el presupuesto. Requiere el despacho automático activado.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 260 }}>
            <Field label="Canal" htmlFor="media-analysis-channel">
              {channels === undefined
                ? <Skeleton h={40} />
                : <Select value={channelId} onChange={setChannelId} options={channelOptions} full />}
            </Field>
          </div>
          <Btn
            variant="primary"
            disabled={controlsDisabled || channelId === '' || startRuns.loading}
            title={controlTooltip ?? (channelId === '' ? 'Elige un canal.' : undefined)}
            onClick={() => { void startRuns.run({ channelId }).catch(() => undefined); }}
          >
            Analizar primero
          </Btn>
        </div>
        {channels !== undefined && channels.length === 0 && (
          <div style={{ marginTop: 12 }}><EmptyState icon="tv" title="Sin canales" body="No hay canales en el espacio de trabajo." /></div>
        )}
      </Panel>

      {/* 5. The month's spend */}
      <Panel title="Costo del análisis de medios" style={{ marginBottom: 24 }}>
        {summary === undefined ? <Skeleton h={120} /> : <MediaAnalysisCostLines summary={summary} />}
      </Panel>

      {/* 6. The switches, read-only */}
      <Panel title="Interruptores" pad={0}>
        <div style={{ padding: '12px 20px', fontSize: 12, color: 'var(--muted-slate)', borderBottom: '1px solid var(--border-cool)' }}>
          Solo lectura: un propietario los cambia con el ejecutor de funciones del panel de Convex (`setSystemConfig`).
        </div>
        {view === undefined ? <div style={{ padding: 16 }}><SkeletonTable rows={6} cols={2} /></div> : (
          Object.entries(view.switches).map(([key, value]) => (
            <div key={key} data-testid="switch-row" data-switch-key={key} style={{ ...TABLE_ROW, gridTemplateColumns: '2fr 1fr' }}>
              <span>
                {SWITCH_LABELS[key] ?? key}
                <span className="t-mono" style={{ display: 'block', fontSize: 11, color: 'var(--muted-slate)' }}>{key}</span>
              </span>
              <span>
                {typeof value === 'boolean'
                  ? <Toggle on={value} disabled />
                  : value === null
                    ? <span style={{ color: 'var(--muted-slate)' }}>predeterminado</span>
                    : <span className="t-mono" style={{ fontSize: 12 }}>{String(value) === '' ? '(vacío)' : String(value)}</span>}
              </span>
            </div>
          ))
        )}
      </Panel>
    </div>
  );
}

/** The routed component: the admin-only screen guard around the body. */
export function MediaAnalysisOps() {
  return (
    <AdminGate>
      <MediaAnalysisOpsBody />
    </AdminGate>
  );
}

export default MediaAnalysisOps;
